import {
  BodyText,
  Callout,
  HeaderText,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Skeleton,
  toast,
} from "@/components/ui";
import { Tooltip } from "@/components/ui/Tooltip";
import { TRACKERS } from "@/constants/Trackers";
import { formatTimeWithTimeZone } from "@/lib/time";
import { trackEvent } from "@/lib/tracking";
import { useDosespotHealthCheck } from "@/network/useDosespotHealthCheck";
import { Bell } from "lucide-react";
import { useEffect, useState } from "react";
import { usePrevious } from "react-use";
import {
  ClinicNotificationLevel,
  useClinicNotifications,
} from "./network/useClinicNotifications";

export const ClinicNotificationsPopover = () => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const { data: clinicNotifications, isPending: isInitialLoading } =
    useClinicNotifications();

  const { data: healthCheck, dataUpdatedAt: dosespotLastChecked } =
    useDosespotHealthCheck();
  const dosespotDown = healthCheck?.vendorResponsive === false;
  const previousDosespotDown = usePrevious(dosespotDown);

  const version = import.meta.env.APP_VERSION;

  useEffect(() => {
    if (dosespotDown && !previousDosespotDown) {
      toast.error(
        "A Dosespot outage has been detected. Follow eRx downtime procedures until Dosespot is back online.",
      );
      return;
    }

    if (previousDosespotDown === true && dosespotDown === false) {
      toast.success("Dosespot is back online.");
    }
  }, [dosespotDown, previousDosespotDown]);

  const hasNotifications =
    dosespotDown || Boolean(clinicNotifications?.notifications.length);

  const getCalloutVariant = (level: ClinicNotificationLevel) => {
    switch (level) {
      case ClinicNotificationLevel.INFO:
        return "info";
      case ClinicNotificationLevel.WARNING:
        return "warning";
      case ClinicNotificationLevel.ERROR:
        return "danger";
      case ClinicNotificationLevel.SUCCESS:
        return "success";
    }
  };

  const getCalloutTitle = (level: ClinicNotificationLevel) => {
    switch (level) {
      case ClinicNotificationLevel.INFO:
        return "Info";
      case ClinicNotificationLevel.WARNING:
        return "Warning";
      case ClinicNotificationLevel.ERROR:
        return "Error";
      case ClinicNotificationLevel.SUCCESS:
        return "Good News Everyone!";
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      trackEvent(TRACKERS.VIEW_NOTIFICATIONS);
    }
    setPopoverOpen(open);
  };

  return (
    <Popover open={popoverOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger>
        <Tooltip tooltipContentText="Notifications" side="right">
          <div className="relative flex items-center justify-center text-white p-2 rounded-lg m-auto w-fit hover:bg-white/25">
            {hasNotifications && (
              <div className="w-3.5 h-3.5 top-[-2px] right-[-4px] absolute  bg-button-danger text-white text-xs rounded-[4px] flex items-center justify-center  cursor-pointer">
                <BodyText
                  size="xs"
                  textColor="white"
                  className="cursor-pointer"
                >
                  •
                </BodyText>
              </div>
            )}
            <Bell size={16} />
          </div>
        </Tooltip>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        className="w-[300px] border-primary border rounded-md p-3 mt-2 gap-2 flex flex-col"
      >
        <HeaderText>Clinic Notifications</HeaderText>
        <Separator />
        {!hasNotifications && (
          <BodyText size="sm" textColor="secondary">
            No clinic notifications.
          </BodyText>
        )}
        {dosespotDown && (
          <Callout
            variant="danger"
            title="Error"
            description="Dosespot is currently down. Follow eRx downtime procedures until Dosespot is back up."
            status={formatTimeWithTimeZone(dosespotLastChecked)}
          />
        )}
        {isInitialLoading && <Skeleton className="h-[100px] w-full" />}
        {clinicNotifications?.notifications.map((notification) => (
          <Callout
            key={notification.clinicNotificationId}
            variant={getCalloutVariant(notification.level)}
            title={getCalloutTitle(notification.level)}
            description={notification.message}
            status={formatTimeWithTimeZone(notification.updatedAt)}
          />
        ))}
        <Separator />
        <BodyText size={"xs"} textColor={"secondary"}>
          Version: {version}
        </BodyText>
      </PopoverContent>
    </Popover>
  );
};
